import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useModal } from './ModalProvider';
import ProfileModal from './ProfileModal';
import ThemeToggle from './ThemeToggle';
import AccentPicker from './AccentPicker';
import SidebarPicker from './SidebarPicker';
import FxToggle from './FxToggle';
import { roleName } from '../utils/format';
import { attPendingCount } from '../api/attendance';
import { NAV_GROUPS } from '../menuConfig';

// NAV_GROUPS + canSeeItem lấy từ module dùng chung (khớp với MobileMenuPage).

export default function Sidebar({ page, setPage }) {
  const { me, logout } = useAuth();
  const { openModal, closeModal } = useModal();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('ft_sidebar_collapsed') === '1');
  const [attPending, setAttPending] = useState(0); // đơn chấm công chờ duyệt -> chấm đỏ ở menu KPI

  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', collapsed);
    localStorage.setItem('ft_sidebar_collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  // Đếm đơn chấm công chờ duyệt (làm mới khi đổi trang + định kỳ 90s).
  useEffect(() => {
    let alive = true;
    const tick = () => attPendingCount().then((d) => alive && setAttPending(d.count || 0)).catch(() => {});
    tick();
    const id = setInterval(tick, 90000);
    return () => { alive = false; clearInterval(id); };
  }, [page]);

  // Hiển thị nav: theo vai trò; mục có "dept" thì cần GĐ hoặc thuộc phòng ban đó.
  const isAdminRole = me.role === 'GD' || me.role === 'CV';
  const isDeptHead = (me.deptHeadOf || []).length > 0; // trưởng phòng ban (kể cả role NV)
  const isOpHead = (me.deptHeadOf || []).includes('Phòng OP'); // trưởng phòng OP: ẩn các báo cáo (trừ Biên bản họp)
  const REPORT_KEYS = ['weekly-report', 'purchase-report', 'business-report', 'cskh-report', 'finance-report'];
  const canSee = (i) => {
    // Mục gdOnly (vd Lionjob): CHỈ một mình Giám đốc (role GD) — chặn trước cả short-circuit CV.
    if (i.gdOnly) return me.role === 'GD';
    // GĐ/CV (Cố vấn) toàn quyền: thấy mọi mục (CV không nằm trong roles của item nên phải bỏ qua sớm).
    if (isAdminRole) return true;
    // Trưởng phòng OP: ẩn toàn bộ báo cáo, chỉ giữ Biên bản họp.
    if (isOpHead && REPORT_KEYS.includes(i.key)) return false;
    if (!i.roles.includes(me.role)) return false;
    // Ẩn với thành viên phòng ban bị chặn (vd Phòng kế toán/DVKH không xem CSKH),
    // NHƯNG nếu kiêm phòng SỞ HỮU module (ownDept) thì vẫn xem được ("thuộc phòng nào có quyền phòng đó").
    if (i.notDept && !isAdminRole) {
      const blockedDepts = Array.isArray(i.notDept) ? i.notDept : [i.notDept];
      const inBlocked = blockedDepts.some((d) => (me.departments || []).includes(d));
      const inOwn = i.ownDept && (me.departments || []).includes(i.ownDept);
      if (inBlocked && !inOwn) return false;
    }
    // Báo cáo: GĐ/CV + mọi role TP + mọi TRƯỞNG PHÒNG BAN đều xem được của nhau (NV thường không).
    if (i.allTP) return isAdminRole || me.role === 'TP' || isDeptHead || (i.ownDept && (me.departments || []).includes(i.ownDept));
    // Báo cáo kinh doanh: GĐ/CV + TRƯỞNG PHÒNG KINH DOANH (role TP có nhóm) + trưởng phòng ban.
    if (i.salesTP) return isAdminRole || (me.role === 'TP' && !!me.group) || isDeptHead;
    // Biên bản họp: GĐ/CV + role TP + trưởng phòng ban + thành viên Phòng CSKH (người ghi).
    if (i.meeting) return isAdminRole || me.role === 'TP' || isDeptHead || (me.departments || []).includes('Phòng CSKH');
    // Bảng lương tổng hợp: GĐ/CV + Kế toán trưởng (trưởng phòng "Phòng kế toán").
    if (i.payrollAdmin) return isAdminRole || (me.deptHeadOf || []).includes('Phòng kế toán');
    // Đề xuất hoàn tiền: NV/TP Kinh doanh + TP DVKH + MỌI NV Phòng kế toán + GĐ.
    // Kế toán VIÊN (không riêng kế toán trưởng) cũng thấy: backend cho cả phòng kế toán xem hết
    // (scopeFilter isAccounting) và duyệt bước "Chờ Kế toán" (canApproveStep) — sidebar giấu là lệch quyền.
    if (i.refund) return isAdminRole || (me.departments || []).includes('Phòng kinh doanh') || (me.departments || []).includes('Phòng kế toán') || (me.deptHeadOf || []).includes('Phòng DVKH');
    if (i.headDept) return isAdminRole || (me.deptHeadOf || []).includes(i.headDept);
    if (i.dept) return me.role === 'GD' || (me.departments || []).includes(i.dept);
    return true;
  };

  return (
    <aside className="sidebar">
      <div className="brand">
        <img className="logo" src="/icon.svg" alt="FunnyTask" />
        <div className="brand-text">
          <h1>FunnyTask</h1>
          <div className="by">By Vũ Duy Khánh</div>
        </div>
        <button className="side-collapse" onClick={() => setCollapsed((v) => !v)} title={collapsed ? 'Mở rộng' : 'Thu gọn'}>
          {collapsed ? '»' : '«'}
        </button>
      </div>
      <nav className="nav">
        {NAV_GROUPS.map((g) => {
          const items = g.items.filter(canSee);
          if (!items.length) return null;
          return (
            <div className="nav-group" key={g.title}>
              <div className="nav-title">{g.title}</div>
              {items.map((i) => (
                <button
                  key={i.key}
                  className={page === i.key ? 'active' : ''}
                  onClick={() => setPage(i.key)}
                  title={i.key === 'kpi' && attPending > 0 ? `${i.label} · ${attPending} đơn chấm công chờ duyệt` : i.label}
                  style={i.key === 'kpi' ? { position: 'relative' } : undefined}
                >
                  <i className={`ti ${i.ico} ico`} aria-hidden="true"></i> <span className="nav-label">{i.label}</span>
                  {i.key === 'kpi' && attPending > 0 && <span style={{ position: 'absolute', top: 8, right: 10, width: 8, height: 8, borderRadius: '50%', background: 'var(--danger)' }} />}
                </button>
              ))}
            </div>
          );
        })}
      </nav>
      <div className="side-foot">
        <div className="who">
          <div className="name">{me.name}</div>
          {/* Nhóm KD -> tên nhóm; không có nhóm -> TÊN PHÒNG BAN; chỉ GĐ/CV mới hiện "Ban giám đốc"
              (trước đây ai không có nhóm đều dính "Ban giám đốc" — kế toán viên cũng bị). */}
          <div className="role">
            {me.group?.name
              || (me.departments || []).join(' · ')
              || (me.role === 'GD' || me.role === 'CV' ? 'Ban giám đốc' : '—')}
          </div>
          <span className={`role-badge ${{ GD: 'role-gd', CV: 'role-cv', TP: 'role-tp', NV: 'role-nv' }[me.displayRole || me.role]}`}>
            {roleName(me.displayRole || me.role)}
          </span>
        </div>
        <button
          className="btn ghost sm"
          style={{ width: '100%', marginTop: 8 }}
          onClick={() => openModal(<ProfileModal onClose={closeModal} />)}
        >
          ⚙️ Đổi thông tin
        </button>
        <ThemeToggle />
        <FxToggle />
        <AccentPicker />
        <SidebarPicker />
        <button className="btn ghost sm" style={{ width: '100%', marginTop: 6 }} onClick={logout}>
          Đăng xuất
        </button>
      </div>
    </aside>
  );
}
